import { LoaderBar, PageHeader } from 'components'
import { Form, Tab } from 'semantic-ui-react'
import { useMemo, useState } from 'react'
import {
    GetCombineTemplateReport,
    GetUserListForGroup,
    GetCustomerList,
} from 'hooks'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
    FormDateField,
    FormDropdownField,
    SubmitCancelButtons,
} from 'elements'
import { CombineTemplateDataTable } from 'page-components'
import {
    DatePickerTypes,
    DateUtility,
    ReportService,
} from 'utility'
import { useNavigate } from 'react-router-dom'

const CombineTemplateSchema = yup.object().shape({
    StartDate: yup
        .string()
        .required('Date required')
        .typeError('Date required'),
    UserId: yup
        .string()
        .required('User is required')
        .nullable(),
    CustomerId: yup
        .string()
        .required('Customer is required')
        .nullable(),
})

export const CombineTemplateReportScreen = () => {
    const {
        data,
        loading,
        filterChanged,
        setData,
        pagination,
        pageChanged,
    } = GetCombineTemplateReport()
    const { options: userOptions } = GetUserListForGroup()
    const { options: customerOptions } = GetCustomerList()
    const navigate = useNavigate()

    const [selectedItems, setSelectedItems] = useState([])
    const [merging, setMerging] = useState(false)

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(CombineTemplateSchema),
        defaultValues: {
            StartDate: new Date(),
            UserId: null,
            CustomerId: null,
        },
    })

    const submit = formData => {
        const selectedDate = new Date(formData.StartDate)
        const startDateISO = DateUtility.dateToLocalISO(
            DateUtility.startDay(selectedDate),
        )
        const endDateISO = DateUtility.dateToLocalISO(
            DateUtility.endDay(selectedDate),
        )

        filterChanged({
            StartDate: startDateISO,
            EndDate: endDateISO,
            Status: 5,
            DashboardReportType: 1,
            UserId: formData.UserId ? parseInt(formData.UserId, 10) : undefined,
            customerIds: formData.CustomerId ? parseInt(formData.CustomerId, 10) : undefined,
        })
    }

    const resetForm = async () => {
        reset({
            StartDate: new Date(),
            UserId: null,
            CustomerId: null,
        })
        setData([])
        setSelectedItems([])
    }

    const handleSelectItem = auditId => {
        setSelectedItems(prev => {
            const existingIndex = prev.findIndex(item => item.auditId === auditId)
            if (existingIndex !== -1) {
                // Remove item and reorder remaining items
                const updated = prev.filter(item => item.auditId !== auditId)
                return updated.map((item, index) => ({
                    ...item,
                    order: index + 1,
                }))
            }
            // Add new item with order
            return [
                ...prev,
                {
                    auditId,
                    order: prev.length + 1,
                },
            ]
        })
    }

    const handleCombineReport = async () => {
        if (selectedItems.length < 2) {
            return
        }

        try {
            setMerging(true)
            // Sort by order and extract auditIds in correct sequence
            const sortedItems = [...selectedItems].sort((a, b) => a.order - b.order)
            const auditIds = sortedItems.map(item => item.auditId).join(';')
            const result = await ReportService.getInspectionReportCumulative(auditIds)

            // Use Cumulative API response directly - navigate with cumulative flag
            // This will prevent the second API call
            const auditId = result?.AuditTemplateId || result?.AuditId || result?.Id
            if (auditId && result?.ReportPath) {
                // Navigate with cumulative flag to use the Cumulative API response
                navigate(`/app/templates/inspection/report/${auditId}?cumulative=true`, {
                    state: { reportData: result },
                })
            } else {
                console.error('No AuditId or ReportPath found in response:', result)
            }

            setSelectedItems([])
        } catch (error) {
            console.error('Error merging reports:', error)
        } finally {
            setMerging(false)
        }
    }

    const panes = useMemo(
        () => [
            {
                menuItem: 'Data',
                render: () => (
                    <>
                        <CombineTemplateDataTable
                            data={data}
                            loading={loading}
                            pagination={pagination}
                            pageChanged={pageChanged}
                            selectedItems={selectedItems}
                            onSelectItem={handleSelectItem}
                            onCombineReport={handleCombineReport}
                            merging={merging}
                        />
                    </>
                ),
            },
        ],
        [data, loading, pagination, pageChanged, selectedItems, merging],
    )

    return (
        <>
            <div className="">
                <PageHeader header="Combine Template" />
                <Form noValidate>
                    <Form.Group widths="equal">
                        <FormDateField
                            name="StartDate"
                            control={control}
                            label="Date"
                            errors={errors.StartDate}
                            type={DatePickerTypes.Single}
                            required
                        />
                        <FormDropdownField
                            name="UserId"
                            control={control}
                            label="User"
                            placeholder="Select User"
                            options={userOptions}
                            errors={errors.UserId}
                            selection
                            required
                        />
                        <FormDropdownField
                            name="CustomerId"
                            control={control}
                            label="Customer"
                            placeholder="Select Customer"
                            options={customerOptions}
                            errors={errors.CustomerId}
                            selection
                            required
                        />
                    </Form.Group>
                    <div className="pt-1">
                        <SubmitCancelButtons
                            onSubmit={handleSubmit(submit)}
                            onCancel={resetForm}
                            className=""
                        />
                    </div>
                </Form>
            </div>
            <div className="my-3">
                <Tab panes={panes} menu={{ secondary: true, pointing: true }} />
                {(loading || merging) && <LoaderBar content={merging ? 'Merging reports...' : 'Loading...'} />}
            </div>
        </>
    )
}
